import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutationState } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  SectionHeader,
  Select,
  Spinner,
  SwitchTrack,
  confirmDialog,
} from "../../../components/ui";
import alejandroSample from "../assets/voices/alejandro.mp3";
import ramiroSample from "../assets/voices/ramiro.mp3";
// `valeria.mp3` es la muestra de MONICA: se grabó cuando esa voz se llamaba
// Valeria, y es la misma voz —solo cambió el nombre—. El archivo conserva su
// nombre viejo para no romper el historial de git por un renombrado, y la
// muestra de la Valeria NUEVA vive en `valeria-nueva.mp3`. Es confuso de leer
// y por eso está escrito: el nombre del archivo NO dice a qué voz pertenece.
import monicaSample from "../assets/voices/valeria.mp3";
import valeriaSample from "../assets/voices/valeria-nueva.mp3";
import michelSample from "../assets/voices/michel.mp3";
import CentinelaWatchPill from "../components/CentinelaWatchPill";
import RefreshSectionButton from "../components/RefreshSectionButton";
import TabLoader from "../components/TabLoader";
import NotificationUsersModal from "../components/NotificationUsersModal";
import TestCallModal from "../components/TestCallModal";
import TestNotificationModal from "../components/TestNotificationModal";
import {
  useCentinelaCatalogs,
  useCentinelaConfig,
  useCentinelaNumeros,
  useCentinelaUnitConfigs,
  useCentinelaSupervisores,
  useCentinelaUsers,
  useCentinelaVoces,
} from "../../../features/centinela/queries";
import {
  CENTINELA_USER_NOTIFICATIONS_MUTATION,
  usePatchCentinelaAlert,
  useUpdateCentinelaConfig,
  useUpdateCentinelaNumero,
  useUpdateCentinelaUnitConfig,
  useUpdateCentinelaUserNotifications,
  useUpdateCentinelaVoz,
} from "../../../features/centinela/mutations";
import { CENTINELA_SUPERVISOR_EVENTS } from "../../../features/centinela/types";
import { CENTINELA_CALL_TYPE_ICONS } from "../theme";
import type {
  CentinelaConfig,
  CentinelaEscalation,
  CentinelaOperationSchedule,
  CentinelaSchedule,
  CentinelaSupervisorEvent,
  CentinelaUnitConfig,
} from "../../../features/centinela/types";
import {
  diffCentinelaConfig,
  diffCentinelaUnitConfig,
  hasKeywordCollision,
  isKeywordRequired,
  mapCentinelaAlertSettings,
  mapCentinelaConfig,
  mapCentinelaUnitConfig,
  servedOperationTimezone,
  toUnitConfigDraft,
} from "../services/mapCentinelaConfig";
import type {
  CentinelaAlertSetting,
  CentinelaUnitConfigDraft,
} from "../services/mapCentinelaConfig";
import { mapCentinelaNumeros } from "../services/mapCentinelaNumeros";
import { useCentinelaConfigGuard } from "../hooks/useCentinelaConfigGuard";
import { useWatchedFleet } from "../hooks/useWatchedFleet";
import { centinelaErrorKey } from "../services/centinelaErrors";
import { isDemoRecord } from "../services/centinelaMocks";
import {
  indexSupervisors,
  mapCentinelaUsers,
  toNotificationsPayload,
} from "../services/mapCentinelaUsers";
import RecipientSchedule from "../components/RecipientSchedule";
import { useRolesStore } from "../../../store/admin/rolesStore";
import type { NotificationUser, SentinelSettings } from "../types";
import {
  DASHCAM_VIEW_MODES,
  useDashcamViewStore,
} from "../../../store/shared/dashcamViewStore";

const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;

const ROUTINE_HOURS = [2, 4, 6, 8, 12] as const;

const CALL_ATTEMPTS = [1, 2, 3, 4, 5] as const;

const CALL_DELAYS = [1, 3, 5, 10, 15, 30] as const;

/**
 * La muestra que se escucha al elegir una voz.
 *
 * Una voz SIN muestra sigue siendo elegible: el selector la ofrece igual y solo
 * se oculta el botón de reproducir. Es preferible a esconder la voz — una voz
 * que existe en el backend y no aparece en el selector es indistinguible de un
 * error, y nadie sabría que puede pedirla.
 *
 * Las cinco tienen muestra. El respaldo se conserva de todos modos: una voz
 * que exista en el backend y no aparezca en el selector es indistinguible de un
 * error, y nadie sabría que puede pedirla.
 */
const VOICE_SAMPLES: Record<string, string> = {
  monica: monicaSample,
  valeria: valeriaSample,
  michel: michelSample,
  alejandro: alejandroSample,
  ramiro: ramiroSample,
};

const formatSeconds = (value: number) => {
  const total = Math.max(Math.floor(value), 0);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

const FALLBACK_ALERT_ICON = "ri-notification-3-line";

const alertIcon = (callType: string | null) =>
  (callType && CENTINELA_CALL_TYPE_ICONS[callType]) || FALLBACK_ALERT_ICON;

const DEFAULT_SETTINGS: SentinelSettings = {
  enabled: true,
  scheduleEnabled: true,
  activeDays: ["mon", "tue", "wed", "thu", "fri"],
  fromTime: "06:00",
  toTime: "22:00",
  routineCallHours: 4,
};

function Toggle({
  checked,
  onChange,
  label,
  description,
  disabled = false,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label?: string;
  description?: string;
  disabled?: boolean;
}) {
  return (
    <label
      className={`flex items-start justify-between gap-3 ${
        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
      }`}
    >
      <span className="min-w-0">
        {label ? (
          <span className="block text-sm font-medium text-content">
            {label}
          </span>
        ) : null}
        {description ? (
          <span className="mt-0.5 block text-xs leading-relaxed text-content-secondary">
            {description}
          </span>
        ) : null}
      </span>
      <span className="relative mt-0.5 shrink-0">
        <input
          type="checkbox"
          className="peer sr-only"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
        />
        <SwitchTrack peer />
      </span>
    </label>
  );
}

interface RecipientDraft {
  channels: string[];
  schedule: CentinelaSchedule | null;
}

function CardSaveBar({
  dirty,
  saving,
  onSave,
  onDiscard,
}: {
  dirty: boolean;
  saving: boolean;
  onSave: () => void;
  onDiscard: () => void;
}) {
  const { t } = useTranslation("common");

  if (!dirty) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-line pt-3">
      <span className="mr-auto flex items-center gap-1.5 text-[11px] font-medium text-warning">
        <i className="ri-error-warning-line text-[13px]" />
        {t("monitoreoIa.config.cardUnsaved")}
      </span>
      <Button
        size="xs"
        variant="secondary"
        disabled={saving}
        onClick={onDiscard}
      >
        {t("monitoreoIa.config.cardDiscard")}
      </Button>
      <Button
        size="xs"
        disabled={saving}
        loading={saving}
        onClick={onSave}
        icon={saving ? undefined : <i className="ri-save-line" />}
      >
        {t("monitoreoIa.config.cardSave")}
      </Button>
    </div>
  );
}

export default function ConfigurarSection() {
  const { t } = useTranslation("common");

  const { watchedUnits } = useWatchedFleet();

  const [settings, setSettings] = useState<SentinelSettings>(DEFAULT_SETTINGS);

  const dmsEnabled = useDashcamViewStore((state) => state.dmsEnabled);
  const viewMode = useDashcamViewStore((state) => state.viewMode);
  const setDmsEnabled = useDashcamViewStore((state) => state.setDmsEnabled);
  const setViewMode = useDashcamViewStore((state) => state.setViewMode);
  const [showTestCallModal, setShowTestCallModal] = useState(false);
  const [showTestNotificationModal, setShowTestNotificationModal] =
    useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const setGuardHasChanges = useCentinelaConfigGuard(
    (state) => state.setHasChanges,
  );
  const registerGuard = useCentinelaConfigGuard((state) => state.register);
  const resetGuard = useCentinelaConfigGuard((state) => state.reset);
  const [previewVoiceId, setPreviewVoiceId] = useState<string | null>(null);
  const [previewProgress, setPreviewProgress] = useState(0);
  const [voiceDurations, setVoiceDurations] = useState<Record<string, number>>(
    {},
  );
  const previewRef = useRef<Record<string, HTMLAudioElement | null>>({});

  const roles = useRolesStore((state) => state.roles);
  const fetchRoles = useRolesStore((state) => state.fetchRoles);

  useEffect(() => {
    if (roles.length === 0) void fetchRoles();
  }, [roles.length, fetchRoles]);

  const roleNameOf = useCallback(
    (roleId: string | null) =>
      roles.find((role) => role.id === roleId)?.name ?? null,
    [roles],
  );

  const usersQuery = useCentinelaUsers();
  const supervisoresQuery = useCentinelaSupervisores();
  const supervisorsById = useMemo(
    () => indexSupervisors(supervisoresQuery.data),
    [supervisoresQuery.data],
  );
  const updateUserNotificationsMutation = useUpdateCentinelaUserNotifications();
  const savingRecipientIds = useMutationState({
    filters: {
      mutationKey: CENTINELA_USER_NOTIFICATIONS_MUTATION,
      status: "pending",
    },
    select: (mutation) =>
      (mutation.state.variables as { userId: string }).userId,
  });
  const notificationUsers = useMemo(
    () => mapCentinelaUsers(usersQuery.data, roleNameOf, supervisorsById),
    [usersQuery.data, roleNameOf, supervisorsById],
  );
  const usersUnresolved = usersQuery.isPending;
  const activeRecipients = useMemo(
    () => notificationUsers.filter((user) => user.enabled),
    [notificationUsers],
  );
  const availableRecipients = useMemo(
    () => notificationUsers.filter((user) => !user.enabled && user.active),
    [notificationUsers],
  );

  const catalogsQuery = useCentinelaCatalogs();
  const alertTypes = useMemo(
    () => catalogsQuery.data?.alert_types ?? [],
    [catalogsQuery.data],
  );

  const patchAlertMutation = usePatchCentinelaAlert();
  const [recipientDrafts, setRecipientDrafts] = useState<
    Record<string, RecipientDraft>
  >({});

  const [alertOverrides, setAlertOverrides] = useState<
    Record<string, CentinelaAlertSetting>
  >({});
  const [pendingAlertId, setPendingAlertId] = useState<string | null>(null);

  const numerosQuery = useCentinelaNumeros();
  const numeros = useMemo(
    () => mapCentinelaNumeros(numerosQuery.data),
    [numerosQuery.data],
  );

  const updateNumeroMutation = useUpdateCentinelaNumero();

  const vocesQuery = useCentinelaVoces();
  const updateVozMutation = useUpdateCentinelaVoz();

  const [numberDraft, setNumberDraft] = useState<string | null>(null);

  const saveOutboundNumber = () => {
    if (!numberDraft) return Promise.resolve();

    const numero = numberDraft;

    return updateNumeroMutation
      .mutateAsync({ numero_monitorista: numero })
      .then(() => {
        setNumberDraft(null);
        toast.success(t("monitoreoIa.config.outboundNumberSaved", { numero }));
      })
      .catch((error) => {
        toast.error(
          t(centinelaErrorKey(error, "monitoreoIa.config.outboundNumberError")),
        );
        throw error;
      });
  };

  const configQuery = useCentinelaConfig();
  const servedConfig = useMemo(
    () => mapCentinelaConfig(configQuery.data),
    [configQuery.data],
  );
  const quietWindow = useMemo(() => {
    const quiet = servedConfig.escalation.supervisor_quiet_hours;
    return quiet.enabled ? { from: quiet.from, to: quiet.to } : null;
  }, [servedConfig]);
  const companyTimezone = useMemo(
    () => servedOperationTimezone(configQuery.data),
    [configQuery.data],
  );

  const servedAlerts = useMemo(
    () => mapCentinelaAlertSettings(configQuery.data, alertTypes),
    [configQuery.data, alertTypes],
  );

  const alertSettingOf = (alertId: string): CentinelaAlertSetting =>
    alertOverrides[alertId] ??
    servedAlerts[alertId] ?? { enabled: false, valor: null };

  const editAlert = (
    alertId: string,
    patch: Partial<CentinelaAlertSetting>,
  ) => {
    const current = alertSettingOf(alertId);
    setAlertOverrides((rows) => ({
      ...rows,
      [alertId]: { ...current, ...patch },
    }));
  };

  const alertDirty = (alertId: string) => {
    const draft = alertOverrides[alertId];
    if (!draft) return false;

    const served = servedAlerts[alertId] ?? { enabled: false, valor: null };
    return draft.enabled !== served.enabled || draft.valor !== served.valor;
  };

  const saveAlert = (alertId: string, label: string) => {
    const draft = alertOverrides[alertId];
    if (!draft || !alertDirty(alertId)) return Promise.resolve();

    const served = servedAlerts[alertId] ?? { enabled: false, valor: null };
    const param =
      alertTypes.find((alert) => alert.id === alertId)?.param ?? null;

    const input: Record<string, boolean | number> = {};
    if (draft.enabled !== served.enabled) input.enabled = draft.enabled;
    if (param && draft.valor !== null && draft.valor !== served.valor) {
      input[param.key] = draft.valor;
    }

    setPendingAlertId(alertId);

    return patchAlertMutation
      .mutateAsync({ alertTypeId: alertId, input })
      .then(() => {
        setAlertOverrides((rows) => {
          const next = { ...rows };
          delete next[alertId];
          return next;
        });
        toast.success(t("monitoreoIa.config.alertSaved", { alert: label }));
      })
      .catch((error) => {
        toast.error(
          t(centinelaErrorKey(error, "monitoreoIa.config.alertSaveError")),
        );
        throw error;
      })
      .finally(() => setPendingAlertId(null));
  };

  const discardAlert = (alertId: string) =>
    setAlertOverrides((rows) => {
      const next = { ...rows };
      delete next[alertId];
      return next;
    });
  const [configDraft, setConfigDraft] = useState<CentinelaConfig | null>(null);
  const config = configDraft ?? servedConfig;

  const updateSchedule = (patch: Partial<CentinelaOperationSchedule>) => {
    setConfigDraft({
      ...config,
      horario_operacion: { ...config.horario_operacion, ...patch },
    });
  };

  const voices = vocesQuery.data?.disponibles ?? [];

  const activeVoiceId = vocesQuery.data?.actual?.id ?? null;

  const [voiceDraft, setVoiceDraft] = useState<string | null>(null);

  const pickedVoiceId = voiceDraft ?? activeVoiceId;

  const voiceDirty = voiceDraft !== null && voiceDraft !== activeVoiceId;

  const saveVoice = () => {
    if (!voiceDraft) return Promise.resolve();

    return updateVozMutation
      .mutateAsync({ voz_id: voiceDraft })
      .then((response) => {
        const name = response?.actual?.nombre ?? voiceDraft;
        setVoiceDraft(null);
        toast.success(t("monitoreoIa.config.voiceChanged", { name }));
      })
      .catch((error) => {
        toast.error(
          t(centinelaErrorKey(error, "monitoreoIa.config.voiceSaveError")),
        );
        throw error;
      });
  };

  const updateConfigMutation = useUpdateCentinelaConfig();
  const updateUnitConfigMutation = useUpdateCentinelaUnitConfig();

  const configurableUnits = useMemo(
    () =>
      watchedUnits
        .filter(({ watch }) => Boolean(watch.deviceId))
        .map(({ unit, watch }) => ({
          deviceId: watch.deviceId as string,
          unitId: unit.id,
          unitName: unit.name,
        })),
    [watchedUnits],
  );

  const deviceIds = useMemo(
    () => configurableUnits.map((row) => row.deviceId),
    [configurableUnits],
  );

  const unitConfigQueries = useCentinelaUnitConfigs(deviceIds);
  const unitConfigsLoading = unitConfigQueries.some((query) => query.isLoading);

  const servedUnitConfigs = new Map(
    deviceIds.map((deviceId, index) => [
      deviceId,
      mapCentinelaUnitConfig(unitConfigQueries[index]?.data, deviceId),
    ]),
  );

  const [unitDrafts, setUnitDrafts] = useState<
    Record<string, CentinelaUnitConfigDraft>
  >({});

  const [phoneFocusId, setPhoneFocusId] = useState<string | null>(null);

  const unitRows = configurableUnits.map((row) => {
    const served = servedUnitConfigs.get(row.deviceId);
    const draft = unitDrafts[row.deviceId] ?? toUnitConfigDraft(served);
    const input = diffCentinelaUnitConfig(served, draft);

    return {
      ...row,
      served,
      draft,
      input,
      dirty: Object.keys(input).length > 0,
    };
  });

  const enabledUnits = unitRows.filter((row) => row.draft.enabled).length;

  const configInput = diffCentinelaConfig(
    servedConfig,
    config,
    configQuery.data,
  );

  const configDirty = Object.keys(configInput).length > 0;

  const dirtyUnits = unitRows.filter((row) => row.dirty);

  const saveConfig = () =>
    updateConfigMutation
      .mutateAsync(configInput)
      .then(() => {
        setConfigDraft(null);
        toast.success(t("monitoreoIa.config.saved"));
      })
      .catch((error) => {
        toast.error(
          t(centinelaErrorKey(error, "monitoreoIa.config.saveError")),
        );
        throw error;
      });

  const discardUnitDraft = (deviceId: string) =>
    setUnitDrafts((rows) => {
      const next = { ...rows };
      delete next[deviceId];
      return next;
    });

  const saveUnitRow = (deviceId: string, unitName: string) => {
    const row = unitRows.find((item) => item.deviceId === deviceId);
    if (!row || !row.dirty) return Promise.resolve();

    if (hasKeywordCollision(row.draft)) {
      toast.error(t("monitoreoIa.config.keywordCollision", { unit: unitName }));
      return Promise.reject(new Error("keyword-collision"));
    }

    return updateUnitConfigMutation
      .mutateAsync({ deviceId, input: row.input })
      .then(() => {
        setUnitDrafts((current) => {
          const next = { ...current };
          delete next[deviceId];
          return next;
        });
        toast.success(t("monitoreoIa.config.unitSaved", { unit: unitName }));
      })
      .catch((error) => {
        toast.error(
          t(centinelaErrorKey(error, "monitoreoIa.config.unitSaveError"), {
            unit: unitName,
          }),
        );
        throw error;
      });
  };

  const stopPreview = () => {
    Object.values(previewRef.current).forEach((audio) => {
      if (!audio) return;
      audio.pause();
      audio.currentTime = 0;
    });
    setPreviewVoiceId(null);
    setPreviewProgress(0);
  };

  useEffect(() => () => stopPreview(), []);

  const togglePreview = (voiceId: string) => {
    const wasPlaying = previewVoiceId === voiceId;
    stopPreview();
    if (wasPlaying) return;

    const audio = previewRef.current[voiceId];
    if (!audio) return;

    setPreviewVoiceId(voiceId);
    audio.play().catch(() => {
      stopPreview();
      toast.error(t("monitoreoIa.config.voicePreviewError"));
    });
  };

  const update = <K extends keyof SentinelSettings>(
    key: K,
    value: SentinelSettings[K],
  ) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const toggleDay = (day: string) => {
    setSettings((prev) => ({
      ...prev,
      activeDays: prev.activeDays.includes(day)
        ? prev.activeDays.filter((item) => item !== day)
        : [...prev.activeDays, day],
    }));
  };

  const updateEscalation = (patch: Partial<CentinelaEscalation>) => {
    setConfigDraft({
      ...config,
      escalation: { ...config.escalation, ...patch },
    });
  };

  const updateSupervisorNotice = (
    event: CentinelaSupervisorEvent,
    value: boolean,
  ) => {
    updateEscalation({
      notify_supervisor_on: {
        ...config.escalation.notify_supervisor_on,
        [event]: value,
      },
    });
  };

  const updateQuietHours = (
    patch: Partial<CentinelaEscalation["supervisor_quiet_hours"]>,
  ) => {
    updateEscalation({
      supervisor_quiet_hours: {
        ...config.escalation.supervisor_quiet_hours,
        ...patch,
      },
    });
  };

  const phoneFieldValue = (
    deviceId: string,
    served: CentinelaUnitConfig | undefined,
    draft: CentinelaUnitConfigDraft,
  ) => {
    const untouched = draft.operator_phone === (served?.operator_phone ?? "");
    const masked = served?.operator_phone_masked ?? "";
    return phoneFocusId !== deviceId && untouched && masked
      ? masked
      : draft.operator_phone;
  };

  const updateUnitDraft = (
    deviceId: string,
    patch: Partial<CentinelaUnitConfigDraft>,
  ) => {
    const current =
      unitDrafts[deviceId] ??
      toUnitConfigDraft(servedUnitConfigs.get(deviceId));
    setUnitDrafts({ ...unitDrafts, [deviceId]: { ...current, ...patch } });
  };

  const saveNotifications = (
    user: NotificationUser,
    changes: {
      enabled?: boolean;
      channels?: string[];
      schedule?: CentinelaSchedule;
    },
  ) =>
    updateUserNotificationsMutation.mutateAsync({
      userId: user.userId,
      input: toNotificationsPayload(user, changes),
    });

  const servedRecipientDraft = (user: NotificationUser): RecipientDraft => ({
    channels: user.channels.filter((c) => c.selected).map((c) => c.id),
    schedule: user.schedule,
  });

  const recipientDraftOf = (user: NotificationUser): RecipientDraft =>
    recipientDrafts[user.userId] ?? servedRecipientDraft(user);

  const editRecipient = (
    user: NotificationUser,
    patch: Partial<RecipientDraft>,
  ) => {
    const current = recipientDraftOf(user);
    setRecipientDrafts((rows) => ({
      ...rows,
      [user.userId]: { ...current, ...patch },
    }));
  };

  const discardRecipient = (userId: string) =>
    setRecipientDrafts((rows) => {
      const next = { ...rows };
      delete next[userId];
      return next;
    });

  const recipientDirty = (user: NotificationUser) => {
    const draft = recipientDrafts[user.userId];
    if (!draft) return false;

    const served = servedRecipientDraft(user);
    const sameChannels =
      draft.channels.length === served.channels.length &&
      draft.channels.every((id) => served.channels.includes(id));
    const sameSchedule =
      JSON.stringify(draft.schedule ?? null) ===
      JSON.stringify(served.schedule ?? null);

    return !sameChannels || !sameSchedule;
  };

  const saveRecipient = (user: NotificationUser) => {
    const draft = recipientDrafts[user.userId];
    if (!draft || !recipientDirty(user)) return Promise.resolve();

    const served = servedRecipientDraft(user);
    const scheduleChanged =
      JSON.stringify(draft.schedule ?? null) !==
      JSON.stringify(served.schedule ?? null);

    const schedule =
      scheduleChanged && draft.schedule
        ? companyTimezone
          ? { timezone: companyTimezone, ...draft.schedule }
          : draft.schedule
        : undefined;

    return saveNotifications(user, {
      channels: draft.channels,
      ...(schedule ? { schedule } : {}),
    })
      .then(() => {
        discardRecipient(user.userId);
        if (scheduleChanged) void supervisoresQuery.refetch();
        toast.success(
          t("monitoreoIa.notifications.recipientSaved", { name: user.name }),
        );
      })
      .catch((error) => {
        toast.error(
          t(centinelaErrorKey(error, "monitoreoIa.notifications.saveError")),
        );
        throw error;
      });
  };

  const toggleChannel = (user: NotificationUser, channelId: string) => {
    const target = user.channels.find((channel) => channel.id === channelId);
    if (!target?.available) return;

    const selected = recipientDraftOf(user).channels;
    editRecipient(user, {
      channels: selected.includes(channelId)
        ? selected.filter((id) => id !== channelId)
        : [...selected, channelId],
    });
  };

  const addRecipients = (added: NotificationUser[]) => {
    setShowUsersModal(false);
    Promise.all(added.map((user) => saveNotifications(user, { enabled: true })))
      .then(() => {
        toast.success(
          t("monitoreoIa.notifications.usersAdded", { count: added.length }),
        );
      })
      .catch(() => {
        toast.error(t("monitoreoIa.notifications.saveError"));
      });
  };

  const removeRecipient = async (user: NotificationUser) => {
    const confirmed = await confirmDialog({
      title: t("monitoreoIa.notifications.removeConfirmTitle"),
      text: t("monitoreoIa.notifications.removeConfirmText", {
        name: user.name,
      }),
      confirmText: t("monitoreoIa.notifications.removeConfirmYes"),
      cancelText: t("common.cancel"),
      tone: "danger",
      icon: "warning",
    });

    if (!confirmed) return;

    discardRecipient(user.userId);

    saveNotifications(user, { enabled: false })
      .then(() => {
        toast.success(
          t("monitoreoIa.notifications.userRemoved", { name: user.name }),
        );
      })
      .catch((error) => {
        toast.error(
          t(centinelaErrorKey(error, "monitoreoIa.notifications.saveError")),
        );
      });
  };

  const dirtyAlertIds = alertTypes
    .map((alert) => alert.id)
    .filter((alertId) => alertDirty(alertId));

  const dirtyRecipients = activeRecipients.filter((user) =>
    recipientDirty(user),
  );

  const anyDirty =
    configDirty ||
    dirtyUnits.length > 0 ||
    dirtyAlertIds.length > 0 ||
    dirtyRecipients.length > 0 ||
    numberDraft !== null ||
    voiceDirty;

  const saveRef = useRef<() => void>(() => {});

  saveRef.current = () => {
    const writes: Promise<unknown>[] = [];

    if (configDirty) writes.push(saveConfig());
    if (numberDraft !== null) writes.push(saveOutboundNumber());
    if (voiceDirty) writes.push(saveVoice());
    dirtyUnits.forEach((row) =>
      writes.push(saveUnitRow(row.deviceId, row.unitName)),
    );
    dirtyAlertIds.forEach((alertId) => {
      const label =
        alertTypes.find((alert) => alert.id === alertId)?.label ?? alertId;
      writes.push(saveAlert(alertId, label));
    });
    dirtyRecipients.forEach((user) => writes.push(saveRecipient(user)));

    if (writes.length === 0) return;

    Promise.allSettled(writes).then((results) => {
      if (results.every((result) => result.status === "fulfilled")) {
        setConfigDraft(null);
      }
    });
  };

  const save = useCallback(() => saveRef.current(), []);

  const discard = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
    setConfigDraft(null);
    setUnitDrafts({});
    setAlertOverrides({});
    setRecipientDrafts({});
    setNumberDraft(null);
    setVoiceDraft(null);
  }, []);

  useEffect(() => {
    setGuardHasChanges(anyDirty);
  }, [anyDirty, setGuardHasChanges]);

  useEffect(() => {
    registerGuard({ save, discard });
    return resetGuard;
  }, [registerGuard, resetGuard, save, discard]);

  return (
    <div className="flex h-full w-full flex-col">
      <SectionHeader
        eyebrow={<CentinelaWatchPill />}
        title={t("monitoreoIa.config.title")}
        subtitle={t("monitoreoIa.config.subtitle")}
        actions={
          <RefreshSectionButton
            loading={
              configQuery.isFetching ||
              catalogsQuery.isFetching ||
              numerosQuery.isFetching ||
              usersQuery.isFetching ||
              unitConfigQueries.some((query) => query.isFetching)
            }
            onRefresh={() => {
              void configQuery.refetch();
              void catalogsQuery.refetch();
              void numerosQuery.refetch();
              void usersQuery.refetch();
              unitConfigQueries.forEach((query) => void query.refetch());
            }}
          />
        }
      />

      <div className="flex-1 overflow-y-auto p-3 sm:p-6">
        <div className="mx-auto w-full max-w-6xl space-y-5">
          <Card
            bordered={false}
            className={`space-y-4 border ${
              configDirty ? "border-warning/50" : "border-line"
            }`}
          >
            <div>
              <h2 className="flex items-center gap-2 text-[15px] font-bold text-content">
                <i className="ri-git-branch-line text-sm" />
                {t("monitoreoIa.config.escalationTitle")}
              </h2>
              <p className="mt-1 text-xs text-content-secondary">
                {t("monitoreoIa.config.escalationDescription")}
              </p>
            </div>

            <div className="space-y-3 rounded-xl border border-line bg-surface p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="flex items-center gap-2 text-[13px] font-semibold text-content">
                    <i className="ri-time-line text-[13px]" />
                    {t("monitoreoIa.config.scheduleTitle")}
                  </h3>
                  <p className="mt-1 text-[11px] text-content-secondary">
                    {t("monitoreoIa.config.scheduleDescription")}
                  </p>
                </div>
                <span className="shrink-0">
                  <Toggle
                    checked={settings.scheduleEnabled}
                    onChange={(value) => update("scheduleEnabled", value)}
                  />
                </span>
              </div>

              <div
                className={`space-y-4 rounded-xl border border-line bg-surface p-4 ${
                  settings.scheduleEnabled ? "" : "opacity-50"
                }`}
              >
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <Input
                    label={t("monitoreoIa.config.fromTime")}
                    type="time"
                    value={config.horario_operacion.desde}
                    disabled={!settings.scheduleEnabled}
                    onChange={(event) =>
                      updateSchedule({ desde: event.target.value })
                    }
                  />
                  <Input
                    label={t("monitoreoIa.config.toTime")}
                    type="time"
                    value={config.horario_operacion.hasta}
                    disabled={!settings.scheduleEnabled}
                    onChange={(event) =>
                      updateSchedule({ hasta: event.target.value })
                    }
                  />
                  <Select
                    label={t("monitoreoIa.config.routineHours")}
                    value={`${settings.routineCallHours}`}
                    disabled={!settings.scheduleEnabled}
                    onChange={(event) =>
                      update("routineCallHours", Number(event.target.value))
                    }
                    options={ROUTINE_HOURS.map((hours) => ({
                      value: `${hours}`,
                      label: t("monitoreoIa.config.everyHours", { hours }),
                    }))}
                  />
                </div>

                <div>
                  <p className="mb-1.5 text-[11px] font-medium text-content-secondary">
                    {t("monitoreoIa.config.scheduleDaysLabel")}
                  </p>

                  <div className="flex flex-wrap gap-2">
                    {DAY_KEYS.map((day) => {
                      const isActive = settings.activeDays.includes(day);

                      return (
                        <button
                          key={day}
                          type="button"
                          disabled={!settings.scheduleEnabled}
                          onClick={() => toggleDay(day)}
                          className={`h-9 w-9 rounded-lg border text-xs font-bold transition-colors disabled:cursor-not-allowed ${
                            isActive
                              ? "border-primary-500 bg-primary-500 text-on-primary"
                              : "border-line bg-app text-content-secondary hover:bg-subtle"
                          }`}
                        >
                          {t(`monitoreoIa.days.${day}`)}
                        </button>
                      );
                    })}
                  </div>

                  <p className="mt-1.5 text-[11px] text-content-faint">
                    {t("monitoreoIa.config.supervisorDaysPending")}
                  </p>
                </div>

                <p className="text-[11px] text-content-faint">
                  {t("monitoreoIa.config.scheduleHint")}
                </p>
              </div>
            </div>

            {configQuery.isLoading ? (
              <TabLoader label={t("monitoreoIa.config.escalationLoading")} />
            ) : (
              <div className="space-y-4">
                <Toggle
                  checked={config.escalation.driver_first}
                  onChange={(value) =>
                    updateEscalation({ driver_first: value })
                  }
                  label={t("monitoreoIa.config.driverFirst")}
                  description={t("monitoreoIa.config.driverFirstHint")}
                />

                <div className="grid gap-3 sm:grid-cols-2">
                  <Select
                    label={t("monitoreoIa.config.maxAttempts")}
                    value={`${config.escalation.max_driver_attempts}`}
                    onChange={(event) =>
                      updateEscalation({
                        max_driver_attempts: Number(event.target.value),
                      })
                    }
                    options={CALL_ATTEMPTS.map((value) => ({
                      value: `${value}`,
                      label: t("monitoreoIa.config.attemptsValue", {
                        count: value,
                      }),
                    }))}
                  />
                  <Select
                    label={t("monitoreoIa.config.attemptDelay")}
                    value={`${config.escalation.attempt_delay_min}`}
                    onChange={(event) =>
                      updateEscalation({
                        attempt_delay_min: Number(event.target.value),
                      })
                    }
                    options={CALL_DELAYS.map((value) => ({
                      value: `${value}`,
                      label: t("monitoreoIa.config.delayValue", {
                        minutes: value,
                      }),
                    }))}
                  />
                </div>

                <div className="space-y-3 rounded-xl border border-line bg-surface p-4">
                  <div>
                    <h3 className="text-[13px] font-semibold text-content">
                      {t("monitoreoIa.config.supervisorTitle")}
                    </h3>
                    <p className="mt-1 text-[11px] text-content-secondary">
                      {t("monitoreoIa.config.supervisorHint")}
                    </p>
                  </div>

                  <div className="grid gap-2 sm:grid-cols-2">
                    {CENTINELA_SUPERVISOR_EVENTS.map((event) => (
                      <Toggle
                        key={event}
                        checked={config.escalation.notify_supervisor_on[event]}
                        onChange={(value) =>
                          updateSupervisorNotice(event, value)
                        }
                        label={t(
                          `monitoreoIa.config.supervisorEvents.${event}`,
                        )}
                      />
                    ))}

                    <Toggle
                      checked
                      disabled
                      onChange={() => {}}
                      label={t("monitoreoIa.config.supervisorEvents.coaccion")}
                      description={t("monitoreoIa.config.coaccionForced")}
                    />
                  </div>
                </div>

                <Toggle
                  checked={config.ajuste_de_ruta.enabled}
                  onChange={(value) => {
                    setConfigDraft({
                      ...config,
                      ajuste_de_ruta: { enabled: value },
                    });
                  }}
                  label={t("monitoreoIa.config.routeAdjust")}
                  description={t("monitoreoIa.config.routeAdjustHint")}
                />

                <div className="space-y-3 rounded-xl border border-line bg-surface p-4">
                  <Toggle
                    checked={config.escalation.supervisor_quiet_hours.enabled}
                    onChange={(value) => updateQuietHours({ enabled: value })}
                    label={t("monitoreoIa.config.quietHoursTitle")}
                    description={t("monitoreoIa.config.quietHoursHint")}
                  />

                  <div className="grid gap-3 lg:grid-cols-2">
                    <Input
                      label={t("monitoreoIa.config.fromTime")}
                      type="time"
                      value={config.escalation.supervisor_quiet_hours.from}
                      disabled={
                        !config.escalation.supervisor_quiet_hours.enabled
                      }
                      onChange={(event) =>
                        updateQuietHours({ from: event.target.value })
                      }
                    />
                    <Input
                      label={t("monitoreoIa.config.toTime")}
                      type="time"
                      value={config.escalation.supervisor_quiet_hours.to}
                      disabled={
                        !config.escalation.supervisor_quiet_hours.enabled
                      }
                      onChange={(event) =>
                        updateQuietHours({ to: event.target.value })
                      }
                    />
                  </div>
                </div>

                <CardSaveBar
                  dirty={configDirty}
                  saving={updateConfigMutation.isPending}
                  onSave={() => saveConfig().catch(() => {})}
                  onDiscard={() => setConfigDraft(null)}
                />
              </div>
            )}
          </Card>
          <Card className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="flex items-center gap-2 text-[15px] font-bold text-content">
                <i className="ri-user-settings-line text-sm" />
                {t("monitoreoIa.notifications.title")}
              </h2>
              <span className="text-[11px] text-content-secondary">
                {t("monitoreoIa.notifications.count", {
                  count: activeRecipients.length,
                })}
              </span>
            </div>
            <p className="text-xs text-content-secondary">
              {t("monitoreoIa.notifications.description")}
            </p>

            {usersUnresolved ? (
              <TabLoader label={t("monitoreoIa.notifications.loading")} />
            ) : activeRecipients.length === 0 ? (
              <EmptyState
                icon="ri-notification-off-line"
                title={t("monitoreoIa.notifications.empty")}
                description={t("monitoreoIa.notifications.emptyDescription")}
              />
            ) : (
              <ul className="grid gap-3 lg:grid-cols-2">
                {activeRecipients.map((recipient) => {
                  const saving = savingRecipientIds.includes(recipient.userId);
                  const draft = recipientDraftOf(recipient);
                  const dirty = recipientDirty(recipient);

                  return (
                    <li
                      key={recipient.userId}
                      aria-busy={saving}
                      className={`rounded-xl border bg-surface p-4 ${
                        dirty ? "border-warning/50" : "border-line"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-3">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-500/10 text-[13px] font-bold text-primary-500">
                            {recipient.name.charAt(0)}
                          </span>
                          <div className="min-w-0">
                            <p className="truncate text-[13px] font-semibold text-content">
                              {recipient.name}
                            </p>
                            {recipient.roleName && (
                              <p className="text-[11px] text-content-secondary">
                                {recipient.roleName}
                              </p>
                            )}
                            {!recipient.active && (
                              <span
                                className="mt-1 inline-flex"
                                title={t(
                                  "monitoreoIa.notifications.inactiveUserHint",
                                )}
                              >
                                <Badge tone="warning" size="sm">
                                  {t("monitoreoIa.notifications.inactiveUser")}
                                </Badge>
                              </span>
                            )}
                            {saving && (
                              <span className="mt-1 flex items-center gap-1.5 text-[10.5px] text-content-faint">
                                <Spinner
                                  size="xs"
                                  className="text-primary-500"
                                  label={t("monitoreoIa.notifications.saving")}
                                />
                                {t("monitoreoIa.notifications.saving")}
                              </span>
                            )}
                          </div>
                        </div>

                        <Button
                          variant="ghost"
                          size="sm"
                          iconOnly
                          aria-label={t("monitoreoIa.actionsCommon.delete")}
                          disabled={saving}
                          onClick={() => removeRecipient(recipient)}
                          icon={<i className="ri-close-line" />}
                        />
                      </div>

                      <p className="mt-2 truncate text-[11px] text-content-faint">
                        {[recipient.phone, recipient.email]
                          .filter(Boolean)
                          .join(" · ") ||
                          t("monitoreoIa.notifications.noEmail")}
                      </p>

                      <div
                        className={`mt-3 grid grid-cols-2 gap-2 sm:gap-1.5 ${
                          [
                            "sm:grid-cols-1",
                            "sm:grid-cols-2",
                            "sm:grid-cols-3",
                            "sm:grid-cols-4",
                          ][Math.min(Math.max(recipient.channels.length, 1), 4) - 1]
                        }`}
                      >
                        {recipient.channels.map((channel) => (
                          <button
                            key={channel.id}
                            type="button"
                            disabled={!channel.available || saving}
                            title={channel.unavailableReason ?? channel.label}
                            onClick={() => toggleChannel(recipient, channel.id)}
                            className={`flex flex-col items-center gap-1 rounded-lg border py-2 transition-colors max-sm:min-h-11 max-sm:flex-row max-sm:justify-center max-sm:gap-2 max-sm:px-2 ${
                              !channel.available
                                ? "cursor-not-allowed border-line-soft bg-subtle/40 text-content-faint opacity-60"
                                : draft.channels.includes(channel.id)
                                  ? "border-primary-500 bg-primary-500 text-on-primary"
                                  : "border-line bg-app text-content-faint hover:bg-subtle"
                            }`}
                          >
                            <i className={`${channel.icon} text-[11px] max-sm:text-sm`} />
                            <span className="text-[10px] font-medium leading-none max-sm:text-xs">
                              {channel.label}
                            </span>
                          </button>
                        ))}
                      </div>

                      {recipient.noPhoneReason && (
                        <p className="mt-2 flex items-start gap-1.5 text-[10.5px] leading-snug text-content-faint">
                          <i className="ri-information-line mt-px text-[11px]" />
                          {recipient.noPhoneReason}
                        </p>
                      )}

                      <RecipientSchedule
                        schedule={draft.schedule}
                        onShift={recipient.onShift}
                        shiftReason={recipient.shiftReason}
                        quietWindow={quietWindow}
                        disabled={saving}
                        onChange={(schedule) =>
                          editRecipient(recipient, { schedule })
                        }
                      />

                      <CardSaveBar
                        dirty={dirty}
                        saving={saving}
                        onSave={() => {
                          saveRecipient(recipient).catch(() => {});
                        }}
                        onDiscard={() => discardRecipient(recipient.userId)}
                      />
                    </li>
                  );
                })}
              </ul>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={() => setShowUsersModal(true)}
                icon={<i className="ri-add-line" />}
              >
                {t("monitoreoIa.notifications.addUser")}
              </Button>

              <Button
                size="sm"
                variant="secondary"
                disabled={activeRecipients.length === 0}
                onClick={() => setShowTestNotificationModal(true)}
                icon={<i className="ri-send-plane-line" />}
              >
                {t("monitoreoIa.notifications.testCta")}
              </Button>
            </div>

            <p className="flex items-start gap-1.5 border-t border-line pt-3 text-[10.5px] leading-relaxed text-content-faint">
              <i className="ri-flashlight-line mt-0.5 shrink-0 text-[12px]" />
              {t("monitoreoIa.config.notificationsSaveHint")}
            </p>
          </Card>

          <Card className="space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <h2 className="flex items-center gap-2 text-[15px] font-bold text-content">
                  <i className="ri-notification-3-line text-sm" />
                  {t("monitoreoIa.config.alertsTitle")}
                </h2>
                <p className="mt-1 text-xs text-content-secondary">
                  {t("monitoreoIa.config.alertsDescription")}
                </p>
              </div>
              <span className="shrink-0 text-[11px] text-content-secondary">
                {t("monitoreoIa.config.alertsCount", {
                  count: alertTypes.length,
                })}
              </span>
            </div>

            {catalogsQuery.isLoading ? (
              <TabLoader label={t("monitoreoIa.config.alertsLoading")} />
            ) : alertTypes.length === 0 ? (
              <EmptyState
                icon="ri-notification-off-line"
                title={t("monitoreoIa.config.alertsEmpty")}
              />
            ) : (
              <ul className="grid gap-2 md:grid-cols-2">
                {alertTypes.map((alert) => {
                  const param = alert.param;
                  const setting = alertSettingOf(alert.id);
                  const saving = pendingAlertId === alert.id;
                  const dirty = alertDirty(alert.id);

                  return (
                    <li
                      key={alert.id}
                      className={`rounded-xl border bg-surface p-3 ${
                        dirty ? "border-warning/50" : "border-line"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-500/10">
                          <i
                            className={`${alertIcon(alert.call_type)} text-sm text-primary-500`}
                          />
                        </span>

                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-[13px] font-semibold text-content">
                              {alert.label}
                            </span>
                            {param ? (
                              <Select
                                size="xs"
                                containerClassName="min-w-[88px] w-auto shrink-0"
                                aria-label={t(
                                  "monitoreoIa.config.alertThresholdLabel",
                                )}
                                value={`${setting.valor ?? param.default}`}
                                disabled={saving}
                                onChange={(event) =>
                                  editAlert(alert.id, {
                                    valor: Number(event.target.value),
                                  })
                                }
                                options={param.options.map((value) => ({
                                  value: `${value}`,
                                  label: t(
                                    "monitoreoIa.config.alertThreshold",
                                    {
                                      value,
                                      unit: param.unit,
                                    },
                                  ),
                                }))}
                              />
                            ) : null}
                            <Badge
                              tone={
                                alert.scope === "viaje" ? "info" : "neutral"
                              }
                              size="sm"
                            >
                              {t(
                                `monitoreoIa.config.alertScopes.${alert.scope}`,
                              )}
                            </Badge>
                            {alert.scope === "viaje" ? (
                              <span className="text-[10.5px] text-content-faint">
                                {t("monitoreoIa.config.alertTripOnly")}
                              </span>
                            ) : null}
                            {saving ? (
                              <span className="flex items-center gap-1.5 text-[10.5px] text-content-faint">
                                <Spinner
                                  size="xs"
                                  className="text-primary-500"
                                  label={t("monitoreoIa.notifications.saving")}
                                />
                                {t("monitoreoIa.notifications.saving")}
                              </span>
                            ) : null}
                          </div>

                          <p className="mt-1 text-[11px] leading-relaxed text-content-secondary">
                            {alert.description}
                          </p>

                          {param?.maxAttempts ? (
                            <p className="mt-1 text-[10.5px] text-content-faint">
                              {t("monitoreoIa.config.alertAttempts", {
                                count: param.maxAttempts.default,
                              })}
                            </p>
                          ) : null}
                        </div>

                        <span className="shrink-0 pt-0.5">
                          <Toggle
                            checked={setting.enabled}
                            disabled={saving}
                            onChange={(value) =>
                              editAlert(alert.id, { enabled: value })
                            }
                          />
                        </span>
                      </div>

                      {dirty ? (
                        <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-line pt-2">
                          <span className="mr-auto text-[10.5px] font-medium text-warning">
                            {t("monitoreoIa.config.cardUnsaved")}
                          </span>
                          <Button
                            size="xs"
                            variant="secondary"
                            disabled={saving}
                            onClick={() => discardAlert(alert.id)}
                          >
                            {t("monitoreoIa.config.cardDiscard")}
                          </Button>
                          <Button
                            size="xs"
                            disabled={saving}
                            loading={saving}
                            onClick={() => {
                              saveAlert(alert.id, alert.label).catch(() => {});
                            }}
                          >
                            {t("monitoreoIa.config.cardSave")}
                          </Button>
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}

            <p className="text-[11px] text-content-faint">
              {t("monitoreoIa.config.alertsHint")}
            </p>
          </Card>
          <Card className="space-y-4">
            <div>
              <h2 className="flex items-center gap-2 text-[15px] font-bold text-content">
                <i className="ri-phone-line text-sm" />
                {t("monitoreoIa.config.callsTitle")}
              </h2>
              <p className="mt-1 text-xs text-content-secondary">
                {t("monitoreoIa.config.callsDescription")}
              </p>
            </div>

            <div
              className={`space-y-2 rounded-xl border bg-surface p-4 ${
                numberDraft !== null ? "border-warning/50" : "border-line"
              }`}
            >
              <p className="text-xs font-medium text-content">
                {t("monitoreoIa.config.outboundNumberLabel")}
              </p>

              {numerosQuery.isLoading ? (
                <TabLoader
                  label={t("monitoreoIa.config.outboundNumberLoading")}
                />
              ) : !numeros.active ? (
                <p className="rounded-xl border border-line bg-surface px-3 py-2.5 text-[11px] text-content-secondary">
                  {t("monitoreoIa.config.outboundNumberMissing")}
                </p>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-2 rounded-xl border border-primary-500/40 bg-primary-500/10 px-3 py-2.5">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-500/15">
                      <i className="ri-phone-line text-sm text-primary-500" />
                    </span>
                    <span className="text-[13px] font-bold tabular-nums text-content">
                      {numeros.active.numero}
                    </span>
                    {numeros.active.country ? (
                      <Badge tone="neutral" size="sm">
                        {numeros.active.country}
                      </Badge>
                    ) : null}
                    {numeros.active.sms ? (
                      <Badge tone="neutral" size="sm">
                        <i className="ri-message-2-line text-[9px]" />
                        SMS
                      </Badge>
                    ) : null}
                    {numeros.active.isDefault ? (
                      <Badge tone="success" size="sm">
                        {t("monitoreoIa.config.outboundNumberDefault")}
                      </Badge>
                    ) : null}
                  </div>

                  {numeros.others.length > 0 ? (
                    <ul className="space-y-1.5">
                      <li className="text-[10px] max-sm:text-[11px] uppercase tracking-wide text-content-faint">
                        {t("monitoreoIa.config.outboundNumberOthers")}
                      </li>
                      {numeros.others.map((row) => (
                        <li key={row.numero}>
                          <button
                            type="button"
                            disabled={updateNumeroMutation.isPending}
                            onClick={() => setNumberDraft(row.numero)}
                            className={`flex w-full flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-left transition-colors hover:bg-subtle disabled:cursor-not-allowed disabled:opacity-50 ${
                              numberDraft === row.numero
                                ? "border-primary-500 bg-primary-500/10"
                                : "border-line bg-surface"
                            }`}
                          >
                            <span className="text-[12px] tabular-nums text-content-secondary">
                              {row.numero}
                            </span>
                            {row.country ? (
                              <Badge tone="neutral" size="sm">
                                {row.country}
                              </Badge>
                            ) : null}
                            {row.sms ? (
                              <Badge tone="neutral" size="sm">
                                <i className="ri-message-2-line text-[9px]" />
                                SMS
                              </Badge>
                            ) : null}
                            <span className="ml-auto flex items-center gap-1.5 text-[10px] max-sm:text-[11px] font-medium text-primary-500">
                              {numberDraft === row.numero ? (
                                <i className="ri-check-line text-[12px]" />
                              ) : null}
                              {t("monitoreoIa.config.outboundNumberUse")}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  <CardSaveBar
                    dirty={numberDraft !== null}
                    saving={updateNumeroMutation.isPending}
                    onSave={() => saveOutboundNumber().catch(() => {})}
                    onDiscard={() => setNumberDraft(null)}
                  />
                </>
              )}

              <p className="text-[11px] text-content-faint">
                {t("monitoreoIa.config.outboundNumberHint")}
              </p>
            </div>

            <div
              className={`space-y-2 rounded-xl border bg-surface p-4 ${
                voiceDirty ? "border-warning/50" : "border-line"
              }`}
            >
              <p className="text-xs font-medium text-content">
                {t("monitoreoIa.config.voiceLabel")}
              </p>

              <div className="grid gap-2 sm:grid-cols-2">
                {voices.map((voice) => {
                  const voiceId = voice.id;
                  const isSelected = pickedVoiceId === voiceId;
                  const isPlaying = previewVoiceId === voiceId;
                  const duration = voiceDurations[voiceId] ?? 0;
                  const sample = VOICE_SAMPLES[voiceId];

                  return (
                    <div
                      key={voiceId}
                      className={`rounded-xl border p-3 transition-colors ${
                        isSelected
                          ? "border-primary-500 bg-primary-500/10"
                          : "border-line bg-surface"
                      }`}
                    >
                      <button
                        type="button"
                        disabled={updateVozMutation.isPending}
                        onClick={() => setVoiceDraft(voiceId)}
                        className="flex w-full items-center gap-2 text-left disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <span
                          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                            isSelected
                              ? "bg-primary-500 text-on-primary"
                              : "bg-subtle text-content-secondary"
                          }`}
                        >
                          {voice.nombre.charAt(0)}
                        </span>
                        <span className="text-[13px] font-semibold text-content">
                          {voice.nombre}
                        </span>
                        {isSelected ? (
                          <i className="ri-check-line ml-auto text-sm text-primary-500" />
                        ) : null}
                      </button>

                      <p className="mt-1 pl-8 text-[10px] max-sm:text-[11px] text-content-secondary">
                        {t(`monitoreoIa.config.voices.${voiceId}.description`, {
                          defaultValue: t(
                            "monitoreoIa.config.voicePresentsAs",
                            {
                              name: voice.se_presenta_como ?? voice.nombre,
                            },
                          ),
                        })}
                      </p>

                      {sample ? (
                        <>
                          <button
                            type="button"
                            onClick={() => togglePreview(voiceId)}
                            aria-label={t("monitoreoIa.config.voicePreview")}
                            className={`mt-2 flex w-full items-center gap-2 rounded-lg px-2 py-1.5 transition-colors ${
                              isPlaying
                                ? "bg-primary-500/15 text-primary-500"
                                : "bg-subtle text-content-secondary hover:bg-line"
                            }`}
                          >
                            <i
                              className={`${isPlaying ? "ri-pause-fill" : "ri-play-fill"} text-sm`}
                            />
                            <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-line">
                              {isPlaying && (
                                <span
                                  className="block h-full rounded-full bg-primary-500"
                                  style={{ width: `${previewProgress}%` }}
                                />
                              )}
                            </span>
                            <span className="shrink-0 text-[10px] max-sm:text-[11px] font-medium">
                              {formatSeconds(
                                isPlaying
                                  ? (previewProgress / 100) * duration
                                  : duration,
                              )}
                            </span>
                          </button>

                          <audio
                            ref={(node) => {
                              previewRef.current[voiceId] = node;
                            }}
                            src={sample}
                            preload="metadata"
                            onLoadedMetadata={(event) => {
                              const { duration: loaded } = event.currentTarget;
                              if (!Number.isFinite(loaded)) return;
                              setVoiceDurations((prev) => ({
                                ...prev,
                                [voiceId]: loaded,
                              }));
                            }}
                            onTimeUpdate={(event) => {
                              const { currentTime, duration: total } =
                                event.currentTarget;
                              if (!total) return;
                              setPreviewProgress((currentTime / total) * 100);
                            }}
                            onEnded={stopPreview}
                          />
                        </>
                      ) : (
                        // Sin muestra grabada. Se dice en vez de dejar el hueco:
                        // una tarjeta más corta que las de al lado se lee como
                        // que algo no cargó, y la voz SÍ se puede elegir.
                        <p className="mt-2 rounded-lg bg-subtle px-2 py-1.5 text-[10px] max-sm:text-[11px] text-content-faint">
                          {t("monitoreoIa.config.voiceNoSample", {
                            defaultValue: "Todavía no hay muestra grabada de esta voz.",
                          })}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <Button
              variant="dashed"
              fullWidth
              onClick={() => setShowTestCallModal(true)}
              icon={<i className="ri-phone-line" />}
            >
              {t("monitoreoIa.config.testCallCta")}
            </Button>

            <CardSaveBar
              dirty={voiceDirty}
              saving={updateVozMutation.isPending}
              onSave={() => saveVoice().catch(() => {})}
              onDiscard={() => setVoiceDraft(null)}
            />

            <p className="text-[11px] text-content-faint">
              {t("monitoreoIa.config.voiceAutoSaveHint")}
            </p>
          </Card>

          <Card className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="flex items-center gap-2 text-[15px] font-bold text-content">
                <i className="ri-user-voice-line text-sm" />
                {t("monitoreoIa.config.perUnitTitle")}
              </h2>
              <span className="text-[11px] text-content-secondary">
                {t("monitoreoIa.config.perUnitCount", {
                  enabled: enabledUnits,
                  total: unitRows.length,
                })}
              </span>
            </div>
            <p className="text-xs text-content-secondary">
              {t("monitoreoIa.config.perUnitDescription")}
            </p>

            {unitConfigsLoading ? (
              <TabLoader label={t("monitoreoIa.config.perUnitLoading")} />
            ) : (
              <div className="lg:overflow-x-auto">
                <div className="space-y-2 lg:min-w-[900px] lg:space-y-1.5">
                  <div className="hidden grid-cols-[1fr_140px_150px_120px_120px_70px_116px] gap-3 px-1 text-[11px] font-medium text-content-faint lg:grid">
                    <span>{t("monitoreoIa.config.columns.unit")}</span>
                    <span>{t("monitoreoIa.config.columns.driver")}</span>
                    <span>{t("monitoreoIa.config.columns.phone")}</span>
                    <span>{t("monitoreoIa.config.columns.keyword")}</span>
                    <span>{t("monitoreoIa.config.columns.duressWord")}</span>
                    <span className="text-center">
                      {t("monitoreoIa.config.columns.active")}
                    </span>
                    <span className="text-center">
                      {t("monitoreoIa.config.columns.save")}
                    </span>
                  </div>

                  {unitRows.map((row) => (
                    <div
                      key={row.deviceId}
                      className={`grid grid-cols-1 gap-3 rounded-xl border p-3 transition-colors lg:grid-cols-[1fr_140px_150px_120px_120px_70px_116px] lg:items-center ${
                        row.dirty
                          ? "border-warning/50 bg-surface"
                          : row.draft.enabled
                            ? "border-line bg-surface"
                            : "border-line bg-subtle"
                      }`}
                    >
                      <p className="flex min-w-0 items-center gap-1.5 truncate text-[13px] font-semibold text-content">
                        {row.unitName}
                        {isDemoRecord(row.unitId) && (
                          <Badge tone="neutral" size="sm">
                            {t("monitoreoIa.demo.badge")}
                          </Badge>
                        )}
                        {isKeywordRequired(row.served) && (
                          <Badge tone="info" size="sm">
                            <i className="ri-key-2-line text-[10px]" />
                            {t("monitoreoIa.config.keywordRequired")}
                          </Badge>
                        )}
                      </p>

                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:contents">
                        <div className="min-w-0">
                          <span className="mb-1 block text-[10px] max-sm:text-[11px] font-medium text-content-faint lg:hidden">
                            {t("monitoreoIa.config.columns.driver")}
                          </span>
                          <Input
                            size="sm"
                            value={row.draft.operator_name}
                            onChange={(event) =>
                              updateUnitDraft(row.deviceId, {
                                operator_name: event.target.value,
                              })
                            }
                            aria-label={t("monitoreoIa.config.columns.driver")}
                          />
                        </div>

                        <div className="min-w-0">
                          <span className="mb-1 block text-[10px] max-sm:text-[11px] font-medium text-content-faint lg:hidden">
                            {t("monitoreoIa.config.columns.phone")}
                          </span>
                          <Input
                            type="tel"
                            size="sm"
                            value={phoneFieldValue(
                              row.deviceId,
                              row.served,
                              row.draft,
                            )}
                            placeholder={t("monitoreoIa.config.phoneMissing")}
                            onFocus={() => setPhoneFocusId(row.deviceId)}
                            onBlur={() => setPhoneFocusId(null)}
                            onChange={(event) =>
                              updateUnitDraft(row.deviceId, {
                                operator_phone: event.target.value,
                              })
                            }
                            aria-label={t("monitoreoIa.config.columns.phone")}
                          />
                        </div>

                        <div className="min-w-0">
                          <span className="mb-1 block text-[10px] max-sm:text-[11px] font-medium text-content-faint lg:hidden">
                            {t("monitoreoIa.config.columns.keyword")}
                          </span>
                          <Input
                            size="sm"
                            value={row.draft.day_word}
                            onChange={(event) =>
                              updateUnitDraft(row.deviceId, {
                                day_word: event.target.value,
                              })
                            }
                            aria-label={t("monitoreoIa.config.columns.keyword")}
                          />
                        </div>

                        <div className="min-w-0">
                          <span className="mb-1 block text-[10px] max-sm:text-[11px] font-medium text-content-faint lg:hidden">
                            {t("monitoreoIa.config.columns.duressWord")}
                          </span>
                          <Input
                            size="sm"
                            value={row.draft.duress_word}
                            onChange={(event) =>
                              updateUnitDraft(row.deviceId, {
                                duress_word: event.target.value,
                              })
                            }
                            aria-label={t("monitoreoIa.config.columns.duressWord")}
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-3 lg:contents">
                        <div className="flex items-center justify-between gap-2 lg:justify-center">
                          <span className="text-[10px] max-sm:text-[11px] font-medium text-content-faint lg:hidden">
                            {t("monitoreoIa.config.columns.active")}
                          </span>
                          <Toggle
                            checked={row.draft.enabled}
                            onChange={(value) =>
                              updateUnitDraft(row.deviceId, { enabled: value })
                            }
                          />
                        </div>

                        <div className="flex items-center justify-end gap-1 lg:justify-center">
                          {row.dirty ? (
                            <>
                              <Button
                                size="xs"
                                disabled={updateUnitConfigMutation.isPending}
                                onClick={() => {
                                  saveUnitRow(row.deviceId, row.unitName).catch(
                                    () => {},
                                  );
                                }}
                              >
                                {t("monitoreoIa.config.cardSave")}
                              </Button>
                              <Button
                                size="xs"
                                variant="ghost"
                                iconOnly
                                aria-label={t("monitoreoIa.config.cardDiscard")}
                                title={t("monitoreoIa.config.cardDiscard")}
                                disabled={updateUnitConfigMutation.isPending}
                                onClick={() => discardUnitDraft(row.deviceId)}
                                icon={<i className="ri-close-line" />}
                              />
                            </>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ))}

                  {unitRows.length === 0 && (
                    <EmptyState
                      icon="ri-truck-line"
                      title={t("monitoreoIa.config.perUnitEmpty")}
                    />
                  )}
                </div>
              </div>
            )}
          </Card>

          <Card className="space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="flex items-center gap-2 text-[15px] font-bold text-content">
                  <i className="ri-camera-lens-line text-sm" />
                  {t("monitoreoIa.config.camerasTitle")}
                </h2>
                <p className="mt-1 text-[11px] text-content-secondary">
                  {t("monitoreoIa.config.camerasDescription")}
                </p>
              </div>
              <span className="shrink-0">
                <Toggle checked={dmsEnabled} onChange={setDmsEnabled} />
              </span>
            </div>

            <div className="space-y-3 rounded-xl border border-line bg-surface p-4">
              <p className="text-[11px] font-medium text-content-secondary">
                {t("monitoreoIa.config.cameraViewLabel")}
              </p>

              <div className="flex flex-wrap gap-2">
                {DASHCAM_VIEW_MODES.map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setViewMode(mode)}
                    className={`rounded-lg border px-3 py-1.5 text-[11px] font-medium transition-colors ${
                      viewMode === mode
                        ? "border-primary-500 bg-primary-500/10 text-primary-500"
                        : "border-line bg-app text-content-secondary hover:bg-subtle"
                    }`}
                  >
                    {t(`monitoreoIa.config.cameraViews.${mode}`)}
                  </button>
                ))}
              </div>

              <p className="text-[11px] text-content-faint">
                {t("monitoreoIa.config.camerasPending")}
              </p>
            </div>
          </Card>
        </div>
      </div>

      {showTestCallModal ? (
        <TestCallModal onClose={() => setShowTestCallModal(false)} />
      ) : null}

      {showTestNotificationModal ? (
        <TestNotificationModal
          users={notificationUsers}
          onClose={() => setShowTestNotificationModal(false)}
        />
      ) : null}

      {showUsersModal ? (
        <NotificationUsersModal
          available={availableRecipients}
          isLoading={usersUnresolved}
          isSaving={updateUserNotificationsMutation.isPending}
          onClose={() => setShowUsersModal(false)}
          onAdd={addRecipients}
        />
      ) : null}
    </div>
  );
}
